<script lang="ts">
	import Modal from './Modal.svelte'
	import { page } from '$app/state'
	import { getContextClient, queryStore } from '@urql/svelte'
	import { browser } from '$app/environment'
	import {
		FeaturedHighlightsTripDocument,
		type FeaturedHighlightsTripQuery,
		type FeaturedHighlightsTripQueryVariables,
	} from '$lib/queries/generated/QueryTrip'
	import { constructCdnUrl } from '$lib/utils/cloudflare-loader'
	import {
		SetTripFeaturedImagesDocument,
		type SetTripFeaturedImagesMutation,
		type SetTripFeaturedImagesMutationVariables,
	} from '$lib/queries/generated/UpdateTrip'
	import { track } from '$lib/utils/track'
	import Button from '../buttons/Button.svelte'
	import modal from '$lib/stores/modal-state'
	import { onMount } from 'svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'
	import { createImageErrorHandler } from '$lib/utils/image-error-handler'

	let title = 'Highlight Block Menu'
	const REQUIRED_IMAGES = 5

	let tripId = $derived(page.params?.slug)

	const client = getContextClient()

	let tripStore = $derived(
		queryStore<FeaturedHighlightsTripQuery, FeaturedHighlightsTripQueryVariables>({
			client,
			query: FeaturedHighlightsTripDocument,
			variables: { tripId },
			pause: !browser || !tripId || !tripId?.startsWith('trp_'),
			requestPolicy: 'network-only',
		}),
	)

	let allHighlights = $derived($tripStore?.data?.trip?.tripHighlights ?? [])

	let featured: Array<{ id: string; position: number }> = $state([])

	onMount(() => {
		featured = allHighlights
			.filter((highlight) => highlight?.featuredImagePosition != null)
			.map((highlight) => ({
				id: highlight?.id ?? '',
				position: highlight?.featuredImagePosition ?? 0,
			}))
			.sort((a, b) => a.position - b.position)
	})

	const setFeaturedImages = (vars: SetTripFeaturedImagesMutationVariables) =>
		client.mutation<SetTripFeaturedImagesMutation, SetTripFeaturedImagesMutationVariables>(
			SetTripFeaturedImagesDocument,
			vars,
		)

	function handleSetFeaturedImages() {
		if (featured.length !== REQUIRED_IMAGES) return

		track('Set Featured Images', { tripId })
		const featuredImages = featured.map((f) => ({
			id: f.id,
			position: f.position,
		}))
		setFeaturedImages({ tripId, featuredImages }).then(() => {
			modal.close()
		})
	}

	function toggleFeatured(id: string) {
		const index = featured.findIndex((f) => f.id === id)
		if (index !== -1) {
			// Allow deselection at any time
			featured = featured.filter((f) => f.id !== id).map((f, i) => ({ ...f, position: i + 1 })) // Reorder positions after removal
		} else if (featured.length < REQUIRED_IMAGES) {
			// Add new image and assign next position
			featured = [...featured, { id, position: featured.length + 1 }]
		}
	}

	let isValid = $derived(featured.length === REQUIRED_IMAGES)
	let remainingCount = $derived(REQUIRED_IMAGES - featured.length)
</script>

<Modal open={true} isLightOverride={false} size="sm:max-w-xl" {title}>
	<p class="my-4 w-full text-sm dark:text-brand-gray-2">
		Select exactly {REQUIRED_IMAGES} images to continue
	</p>
	<div class="flex flex-row flex-wrap gap-1 mt-2">
		{#each allHighlights as highlight}
			{#if highlight?.id && highlight?.highlight?.cloudflareID}
				<button
					class="rounded-lg border-2 overflow-hidden relative touch-manipulation {featured.some(
						(f) => f.id === highlight.id,
					)
						? 'border-brand-primary'
						: featured.length >= REQUIRED_IMAGES
							? 'border-transparent opacity-50 cursor-not-allowed'
							: 'border-transparent hover:border-brand-primary/60'}"
					onclick={stopPropagation(() => toggleFeatured(highlight.id))}
					disabled={!featured.some((f) => f.id === highlight.id) &&
						featured.length >= REQUIRED_IMAGES}
				>
					<img
						class="h-36 w-24 object-cover"
						src={constructCdnUrl(highlight.highlight.cloudflareID)}
						alt={highlight.title ?? ''}
						onerror={createImageErrorHandler(highlight.highlight.cloudflareID, 'GENERAL')}
					/>
					{#if featured.some((f) => f.id === highlight.id)}
						<div
							class="absolute bottom-1 right-1 bg-brand-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold"
						>
							{featured.find((f) => f.id === highlight.id)?.position}
						</div>
					{/if}
				</button>
			{/if}
		{/each}
	</div>
	<p class="mt-4 text-xs w-full flex justify-end dark:text-brand-gray-2">
		Selected: {featured.length} / {REQUIRED_IMAGES}
		{#if remainingCount > 0}
			(need {remainingCount} more)
		{:else}
			✓
		{/if}
	</p>
	<div class="flex flex-row justify-end">
		<Button
			class="my-4"
			theme="brand"
			size="sm"
			onclick={handleSetFeaturedImages}
			disabled={!isValid}
		>
			Set Featured Images
		</Button>
	</div>
</Modal>
